import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useTheme } from '../context/ThemeContext';
import { enterpriseService } from '../services/enterpriseService';
import {
  GraduationCap, BookOpen, Clock, CheckCircle, Play, ArrowRight,
  User, Lock, Mail, AlertCircle, XCircle, Award, ChevronRight,
  Sun, Moon, Globe, RefreshCw, FileText, Video, HelpCircle, LogOut
} from 'lucide-react';

interface TrainingCourse {
  id: number;
  title: string;
  description: string;
  duration_minutes: number;
  content_type: 'video' | 'document' | 'quiz' | 'interactive';
  thumbnail_url?: string;
  modules: TrainingModule[];
  completion_percentage: number;
  due_date?: string;
  status: 'not_started' | 'in_progress' | 'completed';
}

interface TrainingModule {
  id: number;
  title: string;
  type: 'video' | 'document' | 'quiz' | 'interactive';
  duration_minutes: number;
  completed: boolean;
  content_url?: string;
}

interface EmployeeProfile {
  id: number;
  name: string;
  email: string;
  department: string;
  completed_courses: number;
  pending_courses: number;
  overdue_courses: number;
  total_training_hours: number;
  certificates: Certificate[];
}

interface Certificate {
  id: number;
  course_title: string;
  issued_date: string;
  expiry_date?: string;
  certificate_url: string;
}

export const PublicTrainingPortal: React.FC = () => {
  const { t, language, setLanguage } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  
  // Data state
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [courses, setCourses] = useState<TrainingCourse[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<TrainingCourse | null>(null);
  const [activeModule, setActiveModule] = useState<TrainingModule | null>(null);
  const [activeTab, setActiveTab] = useState<'courses' | 'certificates' | 'progress'>('courses');
  
  // Check for token in URL or localStorage
  useEffect(() => {
    const token = searchParams.get('token') || localStorage.getItem('training_token');
    if (token) {
      validateToken(token);
    } else {
      setIsLoading(false);
    }
  }, [searchParams]);
  
  const validateToken = async (token: string) => {
    try {
      // In real implementation, validate token with backend
      // For now, simulate validation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock profile data
      setProfile({
        id: 1,
        name: 'Marko Petrović',
        email: 'marko.petrovic@company.rs',
        department: 'IT Odjeljenje',
        completed_courses: 5,
        pending_courses: 2,
        overdue_courses: 1,
        total_training_hours: 12.5,
        certificates: [
          {
            id: 1,
            course_title: 'Osnove informacione bezbjednosti',
            issued_date: '2025-12-15',
            expiry_date: '2026-12-15',
            certificate_url: '/certificates/1.pdf'
          },
          {
            id: 2,
            course_title: 'GDPR za zaposlene',
            issued_date: '2025-10-20',
            certificate_url: '/certificates/2.pdf'
          }
        ]
      });
      
      // Mock courses
      setCourses([
        {
          id: 1,
          title: 'Phishing awareness trening',
          description: 'Naučite kako da prepoznate i izbjegnete phishing napade',
          duration_minutes: 30,
          content_type: 'interactive',
          completion_percentage: 0,
          due_date: '2026-03-15',
          status: 'not_started',
          modules: [
            { id: 1, title: 'Uvod u phishing', type: 'video', duration_minutes: 5, completed: false },
            { id: 2, title: 'Prepoznavanje lažnih emailova', type: 'video', duration_minutes: 10, completed: false },
            { id: 3, title: 'Praktični primjeri', type: 'document', duration_minutes: 10, completed: false },
            { id: 4, title: 'Test znanja', type: 'quiz', duration_minutes: 5, completed: false }
          ]
        },
        {
          id: 2,
          title: 'Upravljanje lozinkama',
          description: 'Najbolje prakse za kreiranje i čuvanje sigurnih lozinki',
          duration_minutes: 20,
          content_type: 'video',
          completion_percentage: 50,
          status: 'in_progress',
          modules: [
            { id: 5, title: 'Zašto su jake lozinke važne', type: 'video', duration_minutes: 5, completed: true },
            { id: 6, title: 'Kreiranje sigurnih lozinki', type: 'video', duration_minutes: 5, completed: true },
            { id: 7, title: 'Password manageri', type: 'document', duration_minutes: 5, completed: false },
            { id: 8, title: 'Test', type: 'quiz', duration_minutes: 5, completed: false }
          ]
        },
        {
          id: 3,
          title: 'GDPR osnove',
          description: 'Razumijevanje GDPR propisa i vaših obaveza',
          duration_minutes: 45,
          content_type: 'document',
          completion_percentage: 100,
          status: 'completed',
          modules: [
            { id: 9, title: 'Šta je GDPR', type: 'video', duration_minutes: 10, completed: true },
            { id: 10, title: 'Prava pojedinaca', type: 'document', duration_minutes: 15, completed: true },
            { id: 11, title: 'Vaše obaveze', type: 'document', duration_minutes: 15, completed: true },
            { id: 12, title: 'Završni test', type: 'quiz', duration_minutes: 5, completed: true }
          ]
        },
        {
          id: 4,
          title: 'Sigurnost mobilnih uređaja',
          description: 'Zaštitite svoje mobilne uređaje od cyber prijetnji',
          duration_minutes: 25,
          content_type: 'video',
          completion_percentage: 0,
          due_date: '2026-02-28',
          status: 'not_started',
          modules: [
            { id: 13, title: 'Rizici mobilnih uređaja', type: 'video', duration_minutes: 8, completed: false },
            { id: 14, title: 'Sigurnosne postavke', type: 'interactive', duration_minutes: 10, completed: false },
            { id: 15, title: 'Test', type: 'quiz', duration_minutes: 7, completed: false }
          ]
        }
      ]);
      
      localStorage.setItem('training_token', token);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Token validation failed:', error);
      localStorage.removeItem('training_token');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);
    
    try {
      // In real implementation, call backend to validate credentials
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demo, accept any valid-looking email
      if (email.includes('@') && accessCode.length >= 4) {
        validateToken('demo-token-' + Date.now());
      } else {
        setLoginError(language === 'sr' ? 'Nevažeći pristupni podaci' : 'Invalid credentials');
      }
    } catch (error) {
      setLoginError(language === 'sr' ? 'Greška pri prijavi' : 'Login error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('training_token');
    setIsAuthenticated(false);
    setProfile(null);
    setCourses([]);
    setSelectedCourse(null);
  };
  
  const startModule = (course: TrainingCourse, module: TrainingModule) => {
    setSelectedCourse(course);
    setActiveModule(module);
  };
  
  const completeModule = async () => {
    if (!activeModule || !selectedCourse) return;
    
    // Update module completion status
    const updatedModules = selectedCourse.modules.map(m =>
      m.id === activeModule.id ? { ...m, completed: true } : m
    );
    
    const completedCount = updatedModules.filter(m => m.completed).length;
    const newPercentage = Math.round((completedCount / updatedModules.length) * 100);
    
    const newStatus: 'not_started' | 'in_progress' | 'completed' = newPercentage === 100 ? 'completed' : 'in_progress';
    const updatedCourse: TrainingCourse = {
      ...selectedCourse,
      modules: updatedModules,
      completion_percentage: newPercentage,
      status: newStatus
    };
    
    setCourses(courses.map(c => c.id === selectedCourse.id ? updatedCourse : c));
    setSelectedCourse(updatedCourse);
    
    // Move to next module or close
    const currentIndex = updatedModules.findIndex(m => m.id === activeModule.id);
    if (currentIndex < updatedModules.length - 1) {
      setActiveModule(updatedModules[currentIndex + 1]);
    } else {
      setActiveModule(null);
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      case 'in_progress': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30';
      case 'not_started': return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700';
      default: return 'text-gray-600 bg-gray-100';
    }
  };
  
  const getStatusLabel = (status: string) => {
    const labels: Record<string, { sr: string; en: string }> = {
      completed: { sr: 'Završeno', en: 'Completed' },
      in_progress: { sr: 'U toku', en: 'In Progress' },
      not_started: { sr: 'Nije započeto', en: 'Not Started' }
    };
    return labels[status]?.[language] || status;
  };
  
  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'video': return Video;
      case 'document': return FileText;
      case 'quiz': return HelpCircle;
      default: return BookOpen;
    }
  };
  
  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-600 dark:text-gray-400">
            {language === 'sr' ? 'Učitavanje...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }
  
  // Login page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col">
        {/* Header */}
        <header className="p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-800 dark:text-white">Imperion GRC</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Training Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLanguage(language === 'sr' ? 'en' : 'sr')}
              className="p-2 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700 transition-colors"
            >
              <Globe className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700 transition-colors"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </header>
        
        {/* Login Form */}
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                  {language === 'sr' ? 'Portal za obuke' : 'Training Portal'}
                </h2>
                <p className="text-gray-500 dark:text-gray-400">
                  {language === 'sr' 
                    ? 'Prijavite se sa vašim pristupnim podacima' 
                    : 'Sign in with your access credentials'}
                </p>
              </div>
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {language === 'sr' ? 'Email adresa' : 'Email address'}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      placeholder="ime@kompanija.rs"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {language === 'sr' ? 'Pristupni kod' : 'Access code'}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {language === 'sr' 
                      ? 'Pristupni kod ste dobili putem emaila' 
                      : 'Access code was sent to your email'}
                  </p>
                </div>
                
                {loginError && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{loginError}</span>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {language === 'sr' ? 'Prijava' : 'Sign In'}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {language === 'sr' 
                    ? 'Nemate pristupni kod?' 
                    : "Don't have access code?"}
                </p>
                <a href="mailto:support@imperion.io" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                  {language === 'sr' ? 'Kontaktirajte administratora' : 'Contact administrator'}
                </a>
              </div>
            </div>
          </div>
        </main>
        
        {/* Footer */}
        <footer className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
          © 2026 Imperion GRC. {language === 'sr' ? 'Sva prava zadržana.' : 'All rights reserved.'}
        </footer>
      </div>
    );
  }
  
  // Module player modal
  if (activeModule && selectedCourse) {
    const ModuleIcon = getModuleIcon(activeModule.type);
    
    return (
      <div className="fixed inset-0 bg-gray-900 flex flex-col z-50">
        {/* Header */}
        <header className="bg-gray-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveModule(null)}
              className="p-2 rounded-lg hover:bg-gray-700 transition-colors text-gray-300"
            >
              <XCircle className="w-6 h-6" />
            </button>
            <div>
              <p className="text-sm text-gray-400">{selectedCourse.title}</p>
              <h2 className="text-white font-medium">{activeModule.title}</h2>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">
              {activeModule.duration_minutes} {language === 'sr' ? 'min' : 'min'}
            </span>
            <ModuleIcon className="w-5 h-5 text-gray-400" />
          </div>
        </header>
        
        {/* Content area */}
        <main className="flex-1 flex items-center justify-center p-8">
          {activeModule.type === 'video' && (
            <div className="w-full max-w-4xl aspect-video bg-gray-800 rounded-xl flex items-center justify-center">
              <div className="text-center">
                <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">
                  {language === 'sr' ? 'Video sadržaj bi se prikazao ovdje' : 'Video content would display here'}
                </p>
              </div>
            </div>
          )}
          
          {activeModule.type === 'document' && (
            <div className="w-full max-w-4xl bg-white dark:bg-gray-800 rounded-xl p-8 max-h-[70vh] overflow-y-auto">
              <FileText className="w-12 h-12 text-indigo-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{activeModule.title}</h3>
              <div className="prose dark:prose-invert max-w-none">
                <p className="text-gray-600 dark:text-gray-300">
                  {language === 'sr' 
                    ? 'Ovdje bi se prikazao sadržaj dokumenta sa detaljnim uputstvima i informacijama za obuku.' 
                    : 'Document content with detailed instructions and training information would display here.'}
                </p>
              </div>
            </div>
          )}
          
          {activeModule.type === 'quiz' && (
            <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl p-8">
              <HelpCircle className="w-12 h-12 text-purple-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                {language === 'sr' ? 'Test znanja' : 'Knowledge Quiz'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {language === 'sr' 
                  ? 'Interaktivni kviz bi se prikazao ovdje sa pitanjima i odgovorima.' 
                  : 'Interactive quiz with questions and answers would display here.'}
              </p>
              <div className="space-y-3">
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-indigo-500 cursor-pointer">
                  <span className="text-gray-700 dark:text-gray-300">Option A</span>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-indigo-500 cursor-pointer">
                  <span className="text-gray-700 dark:text-gray-300">Option B</span>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-indigo-500 cursor-pointer">
                  <span className="text-gray-700 dark:text-gray-300">Option C</span>
                </div>
              </div>
            </div>
          )}
        </main>
        
        {/* Footer with progress */}
        <footer className="bg-gray-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            {selectedCourse.modules.map((m, i) => (
              <div
                key={m.id}
                className={`w-8 h-1 rounded-full ${
                  m.completed ? 'bg-green-500' : 
                  m.id === activeModule.id ? 'bg-indigo-500' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
          <button
            onClick={completeModule}
            className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg flex items-center gap-2 hover:from-green-600 hover:to-emerald-700 transition-all"
          >
            <CheckCircle className="w-5 h-5" />
            {language === 'sr' ? 'Završi i nastavi' : 'Complete & Continue'}
          </button>
        </footer>
      </div>
    );
  }
  
  // Main dashboard
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-800 dark:text-white">
                {language === 'sr' ? 'Portal za obuke' : 'Training Portal'}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Imperion GRC</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-800 dark:text-white">{profile?.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{profile?.department}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLanguage(language === 'sr' ? 'en' : 'sr')}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Globe className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-white">{profile?.completed_courses}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Završeno' : 'Completed'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-white">{profile?.pending_courses}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Na čekanju' : 'Pending'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-white">{profile?.overdue_courses}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Zakasnelo' : 'Overdue'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-white">{profile?.total_training_hours}h</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Ukupno sati' : 'Total Hours'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('courses')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'courses'
                ? 'bg-indigo-500 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            {language === 'sr' ? 'Moje obuke' : 'My Courses'}
          </button>
          <button
            onClick={() => setActiveTab('certificates')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'certificates'
                ? 'bg-indigo-500 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Award className="w-4 h-4 inline mr-2" />
            {language === 'sr' ? 'Sertifikati' : 'Certificates'}
          </button>
        </div>
        
        {/* Courses list */}
        {activeTab === 'courses' && (
          <div className="grid gap-4 md:grid-cols-2">
            {courses.map(course => (
              <div
                key={course.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(course.status)}`}>
                      {getStatusLabel(course.status)}
                    </span>
                    {course.due_date && (
                      <span className={`text-xs flex items-center gap-1 ${
                        isOverdue(course.due_date) ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        <Clock className="w-3 h-3" />
                        {isOverdue(course.due_date) 
                          ? (language === 'sr' ? 'Zakasnelo' : 'Overdue')
                          : new Date(course.due_date).toLocaleDateString(language === 'sr' ? 'sr-Latn' : 'en')}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    {course.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    {course.description}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {course.duration_minutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {course.modules.length} {language === 'sr' ? 'modula' : 'modules'}
                    </span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-500 dark:text-gray-400">
                        {language === 'sr' ? 'Napredak' : 'Progress'}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        {course.completion_percentage}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                        style={{ width: `${course.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Modules */}
                  <div className="space-y-2">
                    {course.modules.map(module => {
                      const ModuleIcon = getModuleIcon(module.type);
                      return (
                        <button
                          key={module.id}
                          onClick={() => startModule(course, module)}
                          disabled={course.status === 'completed'}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                            module.completed
                              ? 'bg-green-50 dark:bg-green-900/20'
                              : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            module.completed 
                              ? 'bg-green-100 dark:bg-green-900/30' 
                              : 'bg-gray-200 dark:bg-gray-600'
                          }`}>
                            {module.completed ? (
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                            ) : (
                              <ModuleIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <p className={`text-sm font-medium ${
                              module.completed 
                                ? 'text-green-700 dark:text-green-300' 
                                : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              {module.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {module.duration_minutes} min
                            </p>
                          </div>
                          {!module.completed && (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Certificates */}
        {activeTab === 'certificates' && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {profile?.certificates.map(cert => (
              <div
                key={cert.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 dark:text-white">
                      {cert.course_title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {language === 'sr' ? 'Izdat' : 'Issued'}: {new Date(cert.issued_date).toLocaleDateString(language === 'sr' ? 'sr-Latn' : 'en')}
                    </p>
                  </div>
                </div>
                
                {cert.expiry_date && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    {language === 'sr' ? 'Važi do' : 'Valid until'}: {new Date(cert.expiry_date).toLocaleDateString(language === 'sr' ? 'sr-Latn' : 'en')}
                  </p>
                )}
                
                <button className="w-full py-2 px-4 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg text-sm font-medium hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors">
                  {language === 'sr' ? 'Preuzmi sertifikat' : 'Download Certificate'}
                </button>
              </div>
            ))}
            
            {(!profile?.certificates || profile.certificates.length === 0) && (
              <div className="col-span-full text-center py-12">
                <Award className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {language === 'sr' 
                    ? 'Još uvijek nemate sertifikate. Završite obuke da biste ih dobili.' 
                    : "You don't have any certificates yet. Complete courses to earn them."}
                </p>
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        © 2026 Imperion GRC. {language === 'sr' ? 'Sva prava zadržana.' : 'All rights reserved.'}
      </footer>
    </div>
  );
};

export default PublicTrainingPortal;
